import { useState, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { useTagUserMutation } from "common/user/tag";
import { isIndependentNotary } from "common/notary/capacity";
import AlertMessage from "common/core/alert_message";
import Link from "common/core/link";
import { newBrandAnnouncementOrgUrl, newBrandAnnouncementNotaryUrl } from "constants/marketing";
import { captureException } from "util/exception";
import type { ProofPortal } from "constants/app_subdomains";

import type { ProofFrame_viewer_user as User } from "../index.query.graphql";

type RebrandOrgBannerProps = {
  user: User;
  portal: ProofPortal;
};

const REBRAND_ORG_TAG = "rebrand-org-banner";
const SUPPORTED_PORTALS = ["title", "lender", "business"];

export function RebrandOrgBanner({ user, portal }: RebrandOrgBannerProps) {
  const { id: userId } = user;
  const bannerTags = user.tags?.filter((tag) => tag?.tag === REBRAND_ORG_TAG) || [];
  const [showBanner, setShowBanner] = useState(
    Boolean(bannerTags.length === 0) && SUPPORTED_PORTALS.includes(portal),
  );

  const tagUserMutateFn = useTagUserMutation();
  const handleDismissBanner = () => {
    setShowBanner(false);
    try {
      tagUserMutateFn({
        variables: {
          input: {
            userId,
            tag: REBRAND_ORG_TAG,
          },
          userTagList: [REBRAND_ORG_TAG],
        },
      });
    } catch {
      captureException(new Error(`Error tagging rebrand org banner for user ${userId}`));
    }
  };

  function getAnnouncementUrl() {
    const orgBase = `${newBrandAnnouncementOrgUrl}?utm_source=notarize&utm_medium=banner&utm_campaign=proof-launch&utm_content=`;

    if (portal === "business" && isIndependentNotary(user.notaryProfile)) {
      return `${newBrandAnnouncementNotaryUrl}?utm_source=notarize&utm_medium=banner&utm_campaign=proof-launch&utm_content=notary`;
    } else if (portal === "title" || portal === "lender") {
      return `${orgBase}real-estate`;
    }
    return `${orgBase}business`;
  }

  const reactIntlBannerMessageValues = {
    newLook: (text: ReactNode) => <strong>{text}</strong>,
    learnMore: (text: ReactNode) => (
      <>
        <Link white href={getAnnouncementUrl()}>
          {text}
        </Link>
      </>
    ),
  };

  const portalMappingBannerMessage = () => {
    if (portal === "business" && isIndependentNotary(user.notaryProfile)) {
      return (
        <FormattedMessage
          id="7ab4b783-7941-42ad-8007-aa78dadb82a5"
          defaultMessage={
            "<newLook>Introducing Proof!</newLook> Notarize and Proof will offer notaries more opportunities to take part in the digital economy. <learnMore>Learn more</learnMore>"
          }
          values={reactIntlBannerMessageValues}
        />
      );
    } else if (portal === "title" || portal === "lender") {
      return (
        <FormattedMessage
          id="7ab4b783-7941-42ad-8007-aa78dadb82a6"
          defaultMessage={
            "<newLook>Introducing Proof!</newLook> Together, Notarize and Proof will make home closings more secure. <learnMore>Learn more</learnMore>"
          }
          values={reactIntlBannerMessageValues}
        />
      );
    }
    return (
      <FormattedMessage
        id="7ab4b783-7941-42ad-8007-aa78dadb82a7"
        defaultMessage={
          "<newLook>Introducing Proof!</newLook> Together, Notarize and Proof will bring greater security to your most important documents. <learnMore>Learn more</learnMore>"
        }
        values={reactIntlBannerMessageValues}
      />
    );
  };

  return showBanner ? (
    <AlertMessage banner kind="announcement" centerText onClose={handleDismissBanner}>
      {portalMappingBannerMessage()}
    </AlertMessage>
  ) : null;
}
